/**
 * @module Experience / Repdash - back to top directive
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { Directive, HostListener, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { BACK_TO_TOP_CONSTANTS } from '@support/shared/widgets/constants/widgets.constants';

@Directive({
  selector: '[tmoGoTopButton]',
})

/**
 * Component for adding a go-to-top button to scrollable browser content
 */
export class GoTopButtonDirective implements OnInit, OnDestroy {
  /**
   * Unsubscribe all the events on the page
   */
  private ngUnsubscribe: Subject<void>;
  /**
   *  Value of window height
   */
  public viewHeight: number;
  /**
   *  Number to hold value of how much we have scrolled
   */
  public scrollOffset: number;
  /**
   * Value of gcui page height
   */
  public pageHeight: number;
  /**
   *  Combined value of window height and the amount we have scrolled
   */
  public viewAndScroll: number;

  /**
   * Is type of boolean used to navigate at the top of page when true
   */
  @Input() public goToTopRef: string;
  /**
   * Setter to call the scroll method on page load
   */
  @Input() public set setScroll(scrollToReview: boolean) {
    if (scrollToReview) {
      this.scrollOnPageLoad();
    }
  }

  @HostListener('click', ['$event'])
  public onclick($event: Event): void {
    const target = $event.target as HTMLElement;
    if (target.id === 'backToTop') {
      this.el.nativeElement.scrollTop = 0;
    } else {
      /* tslint:disable:no-global-variables */
      if (this.goToTopRef) {
        document.getElementById(this.goToTopRef).scrollIntoView({ behavior: 'smooth' });
        if (document.getElementsByClassName('back-top-button').length) {
          document.getElementsByClassName('back-top-button')[0][
            BACK_TO_TOP_CONSTANTS.STYLE
          ].display = 'none';
        }
      }
    }
  }

  constructor(private el: ElementRef) {
    this.ngUnsubscribe = new Subject<void>();
  }

  public ngOnInit(): void {
    window.addEventListener('scroll', this.scroll, true);
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    window.removeEventListener('scroll', this.scroll, true);
  }
  /**
   * Method to dynamically change position of back-to-top button based on where user is on screen
   * @returns void
   */
  public scroll = (): void => {
    if (document.getElementsByClassName('back-top-button').length) {
      const radix: number = 10;
      this.viewHeight = Number.parseInt(window[BACK_TO_TOP_CONSTANTS.INNERHEIGHT], radix);
      this.scrollOffset = document.getElementsByClassName('gcui')[0].scrollTop; // how much we've scrolled so far
      this.viewAndScroll = this.viewHeight + this.scrollOffset;
      this.pageHeight = document.getElementsByClassName('gcui')[0].scrollHeight;

      if (this.scrollOffset > 0) {
        document.getElementsByClassName('back-top-button')[0][BACK_TO_TOP_CONSTANTS.STYLE].display =
          'block';
        if (this.viewAndScroll < this.pageHeight) {
          document.getElementsByClassName('back-top-button')[0][
            BACK_TO_TOP_CONSTANTS.STYLE
          ].bottom = '0px';
        } else {
          document.getElementsByClassName('back-top-button')[0][
            BACK_TO_TOP_CONSTANTS.STYLE
          ].bottom = '90px';
        }
      } else {
        document.getElementsByClassName('back-top-button')[0][BACK_TO_TOP_CONSTANTS.STYLE].display =
          'none';
      }
    }
  };

  /**ingenico.module.ts
   * Method to scroll into the specified view
   * @returns void
   */
  private scrollOnPageLoad(): void {
    if (this.goToTopRef) {
      document.getElementById(this.goToTopRef).scrollIntoView({ behavior: 'smooth' });
    }
  }
}
